import React from 'react';
import DOMPurify from 'dompurify';
import { CHUNK_WORDS, chunkMs } from '../AIOnboarding/chunkText';

// The community bot's messages in Messenger, shown like /meet's: plain text
// (no bubble), with a new message's words fading in a few at a time.
// Bot messages are stored as HTML (<p>, <br>, <strong>, the odd link) and
// sometimes **bold** — paragraphs, line breaks, bold and links are kept.

// HTML → paragraphs of words, each word carrying its bold / link.
const toParagraphs = (html) => {
  const clean = DOMPurify.sanitize(String(html || ''), { ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'a', 'div', 'span'], ALLOWED_ATTR: ['href'] });
  const doc = new DOMParser().parseFromString(`<div>${clean}</div>`, 'text/html');
  const paras = [[]];
  const newPara = () => { if (paras[paras.length - 1].length) paras.push([]); };
  const pushText = (text, marks) => {
    // **bold** inside the text, and blank lines as paragraph breaks.
    String(text).split(/(\*\*[^*]+\*\*)/g).forEach((part) => {
      const bold = part.startsWith('**') && part.endsWith('**');
      const body = bold ? part.slice(2, -2) : part;
      body.split(/\n\s*\n/).forEach((chunk, ci) => {
        if (ci > 0) newPara();
        chunk.split('\n').forEach((line, li) => {
          if (li > 0) paras[paras.length - 1].push({ br: true });
          (line.match(/\S+/g) || []).forEach(word => paras[paras.length - 1].push({ word, ...marks, bold: marks.bold || bold }));
        });
      });
    });
  };
  const walk = (node, marks) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === 3) return pushText(child.textContent, marks);
      if (child.nodeType !== 1) return;
      const tag = child.tagName.toLowerCase();
      if (tag === 'br') { paras[paras.length - 1].push({ br: true }); return; }
      const block = tag === 'p' || tag === 'div';
      if (block) newPara();
      const href = tag === 'a' ? child.getAttribute('href') : null;
      walk(child, {
        ...marks,
        bold: marks.bold || tag === 'strong' || tag === 'b',
        em: marks.em || tag === 'em' || tag === 'i',
        href: href && /^(https?:\/\/|\/)/i.test(href) ? href : marks.href,
      });
      if (block) newPara();
    });
  };
  walk(doc.body.firstChild, {});
  return paras.filter(p => p.some(t => t.word));
};

const BotText = ({ html, reveal = false }) => {
  const paras = toParagraphs(html);
  const words = paras.flat().filter(t => t.word);
  const step = chunkMs(words.map(t => t.word).join(' '));
  let n = 0;
  return (
    <div className="botText">
      {paras.map((para, pi) => (
        <p key={pi} className="botText-paragraph">
          {para.map((t, i) => {
            if (t.br) return <br key={i} />;
            const delay = Math.floor(n / CHUNK_WORDS) * step;
            n += 1;
            let el = (
              <span key={i} className={reveal ? 'botChunk' : undefined} style={reveal ? { animationDelay: `${delay}ms` } : undefined}>
                {t.word}
              </span>
            );
            if (t.em) el = <em key={i}>{el}</em>;
            if (t.bold) el = <strong key={i}>{el}</strong>;
            if (t.href) el = <a key={i} href={t.href}>{el}</a>;
            return <React.Fragment key={i}>{el}{' '}</React.Fragment>;
          })}
        </p>
      ))}
    </div>
  );
};

export default BotText;
